<template>
  <div>
    <MainPanelUI
      headerText="好友的助力"
      :note="statusMap.note"
      :amount="awardTotalValue"
      :showValueBlock="statusMap.showValueBlock"
      :showPrimaryBtn="statusMap.showPrimaryBtn"
      :showPrimaryBtnBadge="statusMap.showPrimaryBtnBadge"
      :primaryBtnBadgeText="statusMap.primaryBtnBadgeText"
      :defaultAvatarImage="defaultAvatarImage"
      @showCouponDetail="handleShowCouponDetail"
      v-bind="$attrs"
    >
      <template v-slot:inviteProgress>
        <InviteProgress
          size="large"
          :total="currentTaskInfo.total"
          :current="currentTaskInfo.current"
          :emptyAvatarImage="emptyAvatarImage"
          :defaultAvatarImage="defaultAvatarImage"
        />
      </template>
      <template v-slot:countdown v-if="statusMap.showCountdown">
        <CountDown :leftMillsec="taskRemainMillSec" @timeend="handleTimeend" />
      </template>
      <template v-slot:primaryBtn>
        <!-- 帮他助力按钮 -->
        <div
          class="primaryBtnContent"
          v-if="statusMap.primaryBtnType === 'helpHim'"
          @click="handleAcceptTask"
        >
          帮TA助力
        </div>
        <!-- 跳转发起人按钮 -->
        <div
          class="primaryBtnContent"
          v-if="statusMap.primaryBtnType === 'redirectToInviter'"
          @click="handleRedirectToLaunch"
        >
          我也要发起
        </div>
        <!-- 查看我的助力按钮 -->
        <div
          class="primaryBtnContent"
          v-if="statusMap.primaryBtnType === 'goToMyRecords'"
          @click="handleGoToMyRecords"
        >
          查看我的助力
        </div>
      </template>
    </MainPanelUI>
    <CouponDetailModal
      :visible="isShowCouponDetail"
      @close="handleCloseCouponDetail"
      :couponsData="inviteeTaskCouponRewards"
      :isOwned="statusMap.canUseCoupon"
    />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import MainPanelUI from './MainPanelUI.vue';
import { centToYuan } from '@/components/templates/helper';
import InviteProgress from './InviteProgress';
import CouponDetailModal from './CouponDetailModal';
import CountDown from './Countdown';
import {
  isWxMiniProgram,
  isWeixinBrowser,
  parseURL,
  parseQuery,
  stringifyQuery
} from '@/utils';

export default {
  components: { MainPanelUI, InviteProgress, CountDown, CouponDetailModal },
  props: {
    // 空态头像
    emptyAvatarImage: String,
    // 默认头像
    defaultAvatarImage: String
  },
  computed: {
    ...mapState('app/inviteEarnCoupon/invitee', {
      actResult: state => state.actData.result,
      inviteeHasTask: state => state.actData.inviteeHasTask,
      remainTimes: state => state.actData.remainTimes,
      awardTotalValue: state => state.actData.inviteeAmount,
      inviterAwardTotalValue: state => state.actData.inviterAmount,
      inviteeTaskCouponRewards: state => state.actData.inviteeTaskCouponRewards,
      hasProcessingTask: state => state.actData.hasProcessingTask,
      currentTaskInfo: state => state.actData.inviterTaskInfo || {},
      isShowCouponDetail: 'isShowCouponDetail',
      metaInfo: 'metaInfo'
    }),
    taskRemainMillSec() {
      return this.currentTaskInfo.endTime - this.currentTaskInfo.currentTime;
    },
    badgeStr() {
      return `得${centToYuan(this.inviterAwardTotalValue)}元礼包`;
    },
    statusMap() {
      // 参与活动失败
      if (this.actResult !== 'SUCCESS') {
        if (this.actResult === 'AccessActFail') {
          return {
            note: '很遗憾，你无法参与活动',
            showValueBlock: false,
            showCountdown: false,
            showPrimaryBtn: false
          };
        }
        if (this.actResult === 'TaskRiskFail') {
          return {
            note: '您的账号异常，无法领取奖励',
            showValueBlock: false,
            showCountdown: false,
            showPrimaryBtn: false
          };
        }
        if (this.actResult === 'DistributeRewardFail') {
          return {
            note: '活动太火爆，奖励已领完，去其他活动看看吧~',
            showValueBlock: false,
            showCountdown: false,
            primaryBtnType: 'redirectToInviter',
            showPrimaryBtnBadge: true,
            primaryBtnBadgeText: this.badgeStr
          };
        }
        if (this.actResult === 'DistributeRewardDelay') {
          return {
            note: '活动太火爆了，请稍后查看奖励~',
            showCountdown: false,
            primaryBtnType: 'redirectToInviter',
            showPrimaryBtnBadge: true,
            primaryBtnBadgeText: this.badgeStr
          };
        }
        return {
          note: '很遗憾，您无法参与活动',
          showValueBlock: false,
          showCountdown: false,
          showPrimaryBtn: false
        };
      }
      // 本次已经助力过
      if (this.inviteeHasTask === 'thisHasHelp') {
        return {
          note: '助力成功，奖励已到账',
          showCountdown: false,
          primaryBtnType: this.hasProcessingTask
            ? 'goToMyRecords'
            : 'redirectToInviter',
          showPrimaryBtnBadge: true,
          primaryBtnBadgeText: this.badgeStr,
          canUseCoupon: true
        };
      }
      // 之前已经助力过
      if (this.inviteeHasTask === 'lastHasHelp') {
        return {
          note: '你已经帮我助力过啦～',
          showCountdown: false,
          primaryBtnType: this.hasProcessingTask
            ? 'goToMyRecords'
            : 'redirectToInviter',
          showPrimaryBtnBadge: true,
          primaryBtnBadgeText: this.badgeStr,
          canUseCoupon: false
        };
      }
      // 助力次数已用完
      if (this.remainTimes <= 0) {
        return {
          note: '助力次数已经用完啦～',
          showCountdown: false,
          primaryBtnType: this.hasProcessingTask
            ? 'goToMyRecords'
            : 'redirectToInviter',
          showPrimaryBtnBadge: true,
          primaryBtnBadgeText: this.badgeStr
        };
      }
      // 助力任务进行中
      if (this.currentTaskInfo.userTaskStatus === 'progressing') {
        return {
          note: '快帮我助力，送你奖励',
          showCountdown: true,
          primaryBtnType: 'helpHim',
          showPrimaryBtnBadge: false
        };
      }
      // 助力已完成
      if (this.currentTaskInfo.userTaskStatus === 'success') {
        return {
          note: '你来晚了，助力人数已经满了',
          showPrimaryBtnBadge: true,
          primaryBtnBadgeText: this.badgeStr,
          primaryBtnType: this.hasProcessingTask
            ? 'goToMyRecords'
            : 'redirectToInviter'
        };
      }
      // 助力已结束
      if (this.currentTaskInfo.userTaskStatus === 'fail') {
        return {
          note: '你来晚了，助力已结束',
          primaryBtnType: this.hasProcessingTask
            ? 'goToMyRecords'
            : 'redirectToInviter'
        };
      }
      return {};
    }
  },
  mounted() {
    this.reportExpo();
    if (isWeixinBrowser() || isWxMiniProgram()) {
      // 受邀人分享设置为主页
      this.initShareConfig();
    }
  },
  methods: {
    ...mapActions('app/inviteEarnCoupon/invitee', [
      'acceptTask',
      'refreshTaskInfo'
    ]),
    ...mapMutations('app/inviteEarnCoupon/invitee', ['toggleCouponDetail']),
    // 倒计时结束
    handleTimeend() {
      this.refreshTaskInfo();
    },
    // 帮他助力
    async handleAcceptTask() {
      this.$emit('reportEvent', {
        event: 'invited_boost_coupon_click',
        module_name: '帮TA助力'
      });
      // 有返回为助力成功
      const res = await this.acceptTask();
      if (res) {
        this.$emit('reportEvent', {
          event: 'boost_coupon_invited_expo',
          module_name: '受邀者主页',
          page_status: '助力成功'
        });
      }
    },
    // 我也要发起
    handleRedirectToLaunch() {
      this.$router.replace('/');
      this.$emit('reportEvent', {
        event: 'invited_boost_coupon_click',
        module_name: '我也要发起'
      });
    },
    // 查看我的助力
    handleGoToMyRecords() {
      this.$router.replace('/');
      this.$emit('reportEvent', {
        event: 'invited_boost_coupon_click',
        module_name: '查看我的助力'
      });
    },
    handleShowCouponDetail() {
      this.toggleCouponDetail(true);
      this.$emit('reportEvent', {
        event: 'invited_boost_coupon_click',
        module_name: '金额'
      });
    },
    handleCloseCouponDetail() {
      this.toggleCouponDetail(false);
    },
    reportExpo() {
      this.$emit('reportEvent', {
        event: 'boost_coupon_invited_expo',
        module_name: '受邀者主页',
        page_status: this.reportStatus()
      });
    },
    reportStatus() {
      let status;
      if (this.actResult === 'AccessActFail') {
        return '不是目标用户';
      }
      if (
        this.actResult === 'TASKRISKFAIL' ||
        this.actResult === 'DistributeRewardFail'
      ) {
        return '助力成功-无法领取';
      }
      if (this.remainTimes <= 0) {
        return '助力失败-助力次数已满';
      }
      if (this.currentTaskInfo.userTaskStatus === 'success') {
        return '助力失败-人数已满';
      }
      if (this.currentTaskInfo.userTaskStatus === 'fail') {
        return '助力失败-助力已结束';
      }
      if (this.currentTaskInfo.userTaskStatus === 'progressing') {
        return '未助力';
      }
      return status;
    },
    initShareConfig() {
      const shareConfig = (this.metaInfo && this.metaInfo.shareConfig) || {};
      const currentUrl = parseURL(location.href);
      const queryObj = parseQuery();
      currentUrl.hash = '';
        const { invitation_uuid, fromShare, ...otherQueryObj } = queryObj; // eslint-disable-line
      currentUrl.search = `?${stringifyQuery(otherQueryObj)}`;
      this.shareAct({
        title: shareConfig.shareTitle,
        desc: shareConfig.shareDescription,
        img: shareConfig.shareImage,
        link: currentUrl.toString()
      });
    }
  }
};
</script>

<style lang="less" scoped>
.primaryBtnContent {
  width: 100%;
  height: 100%;
  background: transparent;
  line-height: normal;
  .fc;
}
</style>
